<script setup>
import {ref, watch, computed, reactive, defineEmits, onMounted, onUnmounted} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {ArrowLeftIcon, ComputerDesktopIcon, ShareIcon} from '@heroicons/vue/24/solid'

import ButtonLoad from './ButtonLoad.vue'
import Tooltip from './Tooltip.vue'

import {BoardState} from '../assets/js/board.js'

const app_server = import.meta.env.VITE_APP_SERVER;

import {useUserStore } from '@/stores/userstore.js'
const userStore = useUserStore();

import {useMessageStore } from '@/stores/messagestore.js'
const messageStore = useMessageStore();

import {useSSEStore, useMatchSSEStore } from '@/stores/ssestore.js'
const sseStore = useSSEStore();
const matchStore = useMatchSSEStore();

const route = useRoute();
const router = useRouter();

let update_analysis_timeout_id = null;

const dice_unicode = [
    [1, "&#9856;"],
    [2, "&#9857;"],
    [3, "&#9858;"],
    [4, "&#9859;"],
    [5, "&#9860;"],
    [6, "&#9861;"],
];

const engine_configs = [
    ["gnubg", ["1ply", "2ply", "3ply"/*, "4ply", "puzzle"*/]],
    // ["bgblitz", ["1ply"]],
    // ["wildbg", ["1ply"]],
];

const emit = defineEmits([
    'analysis',
    'eq_loss',
]);

const props = defineProps({
    boardstate: {
        type: String,
    },
    moves_played: {
        type: Array,
        default: [],
    },
    delay: {
        type: Number,
        default: 1500,
    },
    show_analysis_time: {
        type: Boolean,
        default: false,
    },
    show_BG: {
        type: Boolean,
        default: false,
    }
})

const state = reactive({
    analysis: null,
});

const extra_data = reactive({
    alt_played: null,
});

var analysis_data = reactive({
    analysis: null,
    analysis_id: null,
    win_probabilities: null,
    config: "1ply",
    engine: "gnubg",
    started_analysis: false,
    analysis_time: 0.0,
    total_analysis_time: 0.0,
});

onMounted(async () => {
    await userStore.loadUser();
    
    state.board = new BoardState();
    if(["A", "P", "D"].includes(state.board.game_state)){
        state.board.game_state = "C"; // TODO
        state.board.cube.action= "N";
    }
    if(state.board.game_state == "C"){
        state.cube_roll = true;
    }

    updateAnalysis();
});

onUnmounted(() => {
});

watch(() => props.boardstate, (new_boardstate) => {
    analysis_data.config = "1ply";
    updateAnalysis();    
});


function handle_analysis(data){
    console.log("Analysis received:", data);
    analysis_data.analysis = data;
    if(data.checker.moves){
        analysis_data.win_probabilities = data.checker.moves[0];
    }
    analysis_data.analysis_id = data.analysis_id;
    analysis_data.started_analysis = false;
    analysis_data.analysis_time = (data.checker.analysis_time || 0) + (data.cube.analysis_time || 0) ;
    analysis_data.total_analysis_time += analysis_data.analysis_time;
    
    emit("analysis", data); 
}

async function getPositionAnalysis(position_id, move_played=null, config=[]){
    if(analysis_data.started_analysis){
        return;
    }
    getPositionTags(position_id);
    const args = new URLSearchParams({
                "move_played": move_played,
                "engine_id": config[0],
                "config": config[1],
            }
        );

    analysis_data.started_analysis = true;
    const response = await fetch(app_server + `/position/${ position_id }/analysis/?${ args.toString()}`, {
        method: "GET",
        mode: "cors",
        headers:{
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("jwt"),
        },
        body: JSON.stringify(),
    });
    const data = await response.json();
    handle_analysis(data.analysis);
    
    if(data.status == "error"){
        return;
    }
}

async function getPositionTags(position_id){
    const response = await fetch(app_server + `/position/${ position_id }/classify/`, {
        method: "GET",
        mode: "cors",
        headers:{
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("jwt"),
        },
    });
    const data = await response.json();
    
    if(data.status == "success"){
        analysis_data.tags = data.tags;
    }
    console.log("Classifier: ", data);
}

function get_playercolor(){
    return state.board.opponent[state.board.color];
}

function updateAnalysis(){
    const engine_config_dict = Object.fromEntries(engine_configs);
    if(!engine_config_dict[analysis_data.engine].includes(analysis_data.config)){
        analysis_data.config = engine_config_dict[analysis_data.engine][0];
    }

    if(update_analysis_timeout_id){
        clearTimeout(update_analysis_timeout_id);
    }
    const board = new BoardState(props.boardstate);
    if(["PR", "G", "M"].includes(board.game_state)){
        return;
    }
    else if(["A", "P", "D"].includes(board.game_state)){
        board.game_state = "C"; // TODO
        board.cube.action= "N";
    }

    update_analysis_timeout_id = setTimeout(() => {
        getPositionAnalysis(board.toPositionString(), null, 
            [analysis_data.engine, analysis_data.config]
        );
    }, props.delay);
}

async function copy_xgid_to_clipboard(){
    const xgid = state.current_board.to_xgid();
     
    if(window.isSecureContext){
        await navigator.clipboard.writeText(xgid);
        messageStore.alertUser("Share", "Copied XGID to clipboard.");
    }
}
async function copy_ogid_to_clipboard(){
    const ogid = state.current_board.toPositionString();
     
    if(window.isSecureContext){
        await navigator.clipboard.writeText(ogid);
        messageStore.alertUser("Share", "Copied OGID to clipboard.");
    }
}

</script>
<template>
<div>
<div class="relative flex flex-col gap-y-4 w-64 px-4" :key="analysis_data.analysis_id">
<div class="z-10 opacity-40 bg-accent-3-color w-full h-full absolute left-0 
            top-0 flex justify-center items-center"
    v-if="analysis_data.started_analysis"
>
    <div class="text-2xl text-text-color font-bold">Analysing...</div>
</div>

<div class="flex flex-col gap-y-2 relative">
<div class="flex gap-x-2" v-if="false">
    <button v-for="eg in engine_configs"
        class="btn btn-blue" :disabled="eg[0] == analysis_data.engine"
        @click="analysis_data.engine = eg[0]; updateAnalysis();"
    >
        {{ eg[0] }}
    </button>
</div>
<div class="flex gap-x-2">
    <button v-for="config in Object.fromEntries(engine_configs)[analysis_data.engine]"
        class="btn btn-blue" 
        :disabled="config == analysis_data.config"
        @click="analysis_data.config = config; updateAnalysis();"
    >
        {{ config }}
    </button>
</div>
</div>

<div class=" max-w-64 text-board-text-color">
<div
class="" 
v-if="analysis_data.win_probabilities"
>
<table class="w-full mb-6">
    <tbody>
    <tr class="font-thin">
        <td>Win</td>
        <td>Win G</td>
        <td v-if="props.show_BG">Win BG</td>
        <td>Lose G</td>
        <td v-if="props.show_BG">Lose BG</td>
        <td>Equity</td>
    </tr>
    
    <tr>
        <td class="text-left">
            {{ analysis_data.win_probabilities["W"].toFixed(3) }}
        </td>
        <td class="text-left">
            <Tooltip :text="'BG: ' + analysis_data.win_probabilities['WBG'].toFixed(3)">
                {{ analysis_data.win_probabilities["WG"].toFixed(3) }}
            </Tooltip>
        </td>
        <td class="text-left" v-if="props.show_BG">
            {{ analysis_data.win_probabilities["WBG"].toFixed(3) }}
        </td>
        <td class="text-left">
            <Tooltip :text="'BG: ' + analysis_data.win_probabilities['LBG'].toFixed(3)">
                {{ analysis_data.win_probabilities["LG"].toFixed(3) }}
            </Tooltip>
        </td>
        <td class="text-left" v-if="props.show_BG">
            {{ analysis_data.win_probabilities["LBG"].toFixed(3) }}
        </td>
        <td class="text-left">
            {{ analysis_data.win_probabilities["EQ"].toFixed(3) }}
        </td>
    </tr>
    </tbody>
</table>
</div>

<table class="w-full max-w-64" 
    v-if="analysis_data.analysis != null && analysis_data.analysis.checker"
    :set="best_eq = analysis_data.analysis.checker.moves[0].EQ"
>
    <tbody>
    <tr v-for="move in analysis_data.analysis.checker.moves.slice(0,5)">
        <td class="py-1 flex items-center gap-x-1"
            :class="{
                    'font-semibold': move.alt == 1,
                    }"
            >
            <div v-if="extra_data.alt_played == move.alt - 1 || true"
                  class="rounded-full size-5 p-0  flex items-center justify-center"
                  :class="{
                      'error': 0.020 < Math.abs(move.EQ - best_eq) && Math.abs(move.EQ - best_eq) <= 0.80,
                      'blunder': 0.080 < Math.abs(move.EQ - best_eq),
                  }"
            />
            {{ move.move.repr }}                        
        </td>
            <td class="pl-4 text-right tabular-nums">
                {{ (move.EQ - best_eq).toFixed(3) }}
            </td>
    </tr>
    </tbody>
</table>
<table class="w-full max-w-64" 
    v-else-if="analysis_data.analysis != null && analysis_data.analysis.cube && analysis_data.analysis.cube.double">
    <tbody>
    <tr v-for="move in analysis_data.analysis.cube.double['cube recommendation'].actions"
        :class="{'font-semibold': analysis_data.analysis.cube.double['cube recommendation'].best == move[0]}"
    >
        <td>
            {{ move[0] }}                        
        </td>
        <td class="pl-4 text-right">
            {{ move[1].toFixed(3) }}
        </td>
        <td class="pl-4 text-right">
            {{ move[2].toFixed(3) }}
        </td>
    </tr>
    </tbody>
</table>
<div v-else>
    No analysis available. Is this a valid position?
</div>
</div>
<!--
<div 
    class="font-thin text-light-color flex gap-x-1 w-full" 
    v-if="analysis_data.tags">
    <span class="rounded px-2 py-0.5 bg-accent-3-color"
        v-for="tag in analysis_data.tags">
        {{ tag }}
    </span>
</div>
<div class="font-thin" v-else>
    No tags
</div>
-->
</div>

<div v-if="props.show_analysis_time"
    class="font-thin order-first md:order-none max-w-64">
    It took {{ analysis_data.analysis_time.toFixed(2)}} seconds to analyse this position and
    you used 
    <span :class="{
            'text-red-500': analysis_data.total_analysis_time > 10, 
            'font-semibold': analysis_data.total_analysis_time > 5, 
                }">
        {{ analysis_data.total_analysis_time.toFixed(2) }}
    </span> 
    seconds of server time.
</div>
</div>
</template>

<style scoped>
.error{
    border: solid .15em color-mix(in srgb,var(--text-color) 30%,var(--error-color));
    color: #fff;
    color: color-mix(in srgb,white 80%,var(--error-color));
    background-color: var(--error-color);
}
.error::after {
    content: "?";
}

.blunder{
    border: solid .15em color-mix(in srgb,var(--text-color) 30%,var(--blunder-color));
    color: #fff;
    color: color-mix(in srgb,white 80%,var(--blunder-color));
    background-color: var(--blunder-color);
    letter-spacing: -.1em;
}
.blunder::after {
    transform: translate(-.03em);
    content: "??";
}

.brilliant{
    border: solid .15em color-mix(in srgb,var(--text-color) 30%,var(--brilliant-color));
    color: #fff;
    color: color-mix(in srgb,white 80%,var(--brilliant-color));
    background-color: var(--brilliant-color);
}
.brilliant::after {
    content: "!!";
}

.critical{
    border: solid .15em color-mix(in srgb,var(--text-color) 30%,var(--critical-color));
    color: #fff;
    color: color-mix(in srgb,white 80%,var(--critical-color));
    background-color: var(--critical-color);
}
.critical::after {
    content: "!";
}

.dubious{
    border: solid .15em color-mix(in srgb,var(--text-color) 30%,var(--error-color));
    color: #fff;
    color: color-mix(in srgb,white 80%, var(--error-color));
    background-color: color-mix(in srgb, white 20%, var(--error-color));
}
.dubious::after {
    content: "?!";
}
</style>

